import React, { useEffect, useMemo } from 'react';
import { SelectionMode, Selection, IColumn, IObjectWithKey } from '@fluentui/react';
import { isNotNullOrWhitespace } from '../../utils';
import { BwjTableUi, IBwjTableStyles } from './ui';

export enum ColumnSortDirection {
  None,
  Ascending,
  Descending,
}

// SUPER IMPORTANT: These props must be immutable for the lifetime of the table, or the ability to
// clear/toggle selections is lost!
// Of course, onSelectionChanged may need to be defined within the component function.  In this case, wrap it in a useCallback
export interface ISelectableRowProps<T> {
  selectionMode: SelectionMode; // none, single, multiple
  onSelectionChanged: (selected: T[]) => void;
  selectionPreservedOnEmptyClick: boolean;
  //accessibility
  ariaLabelForSelectionColumn?: string;
  ariaLabelForSelectAllCheckbox?: string;
  checkButtonAriaLabel?: string;
}
  
export interface IBwjTableProps<T> {
  items: T[];
  columns: IColumn[];
  selectionProps?: ISelectableRowProps<T>;
  // Whenever clearSelectionPrompt changes, it triggers a reset of the current selection.
  // This 'side effect' effectively provides an API that the parent component can use to control this component,
  // and that, my friends, is this value's only purpose
  clearSelectionPrompt?: number | string;
  // Triggers an inversion of the current selection when changed, similar to clearSelectionPrompt
  invertSelectionPrompt?: number | string;
  getKey: (item: T, index?: number | undefined) => string;
  onItemInvoked?: (item?: T, index?: number | undefined, ev?: Event | undefined) => void; // occurs when a row is double-clicked on
  numberOfPages?: number;
  currentPageIndex?: number;
  onPageChangeRequested?: (pageIndex: number) => void;
  onColumnSortRequested?: (columnKey: string, direction: ColumnSortDirection) => void;
  showLoadingIndicator?: boolean;
  tableStyles?: IBwjTableStyles;
  setRowBackgroundColor?: (item?: T, index?: number) => string | null | undefined;
  semiFlexWidth?: boolean;
  semiFlexHeight?: boolean;
}

interface ISelectionUiProps {
  selection: Selection | undefined;
  selectionMode: SelectionMode | undefined;
  selectionPreservedOnEmptyClick: boolean | undefined;
  ariaLabelForSelectionColumn: string | undefined;
  ariaLabelForSelectAllCheckbox: string | undefined;
  checkButtonAriaLabel: string | undefined;
}

const toSelectionUiProps = (
  selectionProps: ISelectableRowProps<any> | undefined,
  getKey: (item: any, index?: number | undefined) => string): ISelectionUiProps => {

  // no UI props if not selection props, or selection not enabled
  if(!selectionProps || selectionProps.selectionMode === SelectionMode.none) {
    return {
      selection: undefined,
      selectionMode: undefined,
      selectionPreservedOnEmptyClick: undefined,
      ariaLabelForSelectAllCheckbox: undefined,
      ariaLabelForSelectionColumn: undefined,
      checkButtonAriaLabel: undefined, 
    };
  }

  const selectionWrapper: { selection: Selection | undefined } = { selection: undefined };
  const selection = new Selection({
    onSelectionChanged: () => selectionProps.onSelectionChanged(selectionWrapper.selection!.getSelection() as any[]),
    getKey: (item: IObjectWithKey, index?: number) => getKey(item as any, index),
  });
  selectionWrapper.selection = selection;

  const ariaLabelForSelectAllCheckbox = isNotNullOrWhitespace(selectionProps.ariaLabelForSelectAllCheckbox) ?
  selectionProps.ariaLabelForSelectAllCheckbox : 'Toggle selection for all items';
  const ariaLabelForSelectionColumn = isNotNullOrWhitespace(selectionProps.ariaLabelForSelectionColumn) ?
  selectionProps.ariaLabelForSelectionColumn : 'Toggle selection';
  const checkButtonAriaLabel = isNotNullOrWhitespace(selectionProps.checkButtonAriaLabel) ?
  selectionProps.checkButtonAriaLabel : 'Select row';

  return {
    selection,
    selectionMode: selectionProps.selectionMode,
    selectionPreservedOnEmptyClick: selectionProps.selectionPreservedOnEmptyClick,
    ariaLabelForSelectAllCheckbox,
    ariaLabelForSelectionColumn,
    checkButtonAriaLabel, 
  };
};

export const BwjTableComponent: React.FunctionComponent<IBwjTableProps<any>> = (props) => {
  const { selectionProps, getKey, clearSelectionPrompt, invertSelectionPrompt } = props;

  const selectionUiProps = useMemo(
    () => toSelectionUiProps(selectionProps, getKey),
    [selectionProps, getKey]);

  // handles command to clear selection
  useEffect(() => {
    if(!!selectionUiProps.selection) {
      selectionUiProps.selection.setAllSelected(false);
    }
  }, [selectionUiProps, clearSelectionPrompt]);

  // handles command to toggle selection
  useEffect(() => {
    if(!!selectionUiProps.selection && selectionUiProps.selection.getSelection().length > 0) {
      selectionUiProps.selection.toggleAllSelected();
    }
  }, [selectionUiProps, invertSelectionPrompt]);
  
  const onColumnClicked = (column: IColumn) => {
    if(!props.onColumnSortRequested) { return; }

    // columns requested for sort not previously sorted,
    // or columns that were previously sorted descending are sorted ascending
    const sortDir = !column.isSorted || !!column.isSortedDescending ?
      ColumnSortDirection.Ascending : ColumnSortDirection.Descending;
    
    props.onColumnSortRequested(column.key, sortDir);
  }

  return (
    <BwjTableUi
      items={props.items}
      columns={props.columns}
      onColumnClicked={onColumnClicked}
      getKey={props.getKey}
      onItemInvoked={props.onItemInvoked}
      numberOfPages={props.numberOfPages}
      currentPageIndex={props.currentPageIndex}
      onPageChangeRequested={props.onPageChangeRequested}
      tableStyles={props.tableStyles}
      setRowBackgroundColor={props.setRowBackgroundColor}
      semiFlexWidth={props.semiFlexWidth}
      semiFlexHeight={props.semiFlexHeight}
      selection={selectionUiProps.selection}
      selectionMode={selectionUiProps.selectionMode}
      selectionPreservedOnEmptyClick={selectionUiProps.selectionPreservedOnEmptyClick}
      ariaLabelForSelectionColumn={selectionUiProps.ariaLabelForSelectionColumn}
      ariaLabelForSelectAllCheckbox={selectionUiProps.ariaLabelForSelectAllCheckbox}
      checkButtonAriaLabel={selectionUiProps.checkButtonAriaLabel}
    />
  );
};
